'use client'

import { useEffect, useState } from 'react'

/**
 * Who is signed in, read from the browser — WITHOUT better-auth's client.
 *
 * The header is on every page, so whatever it imports is in the first-load
 * bundle of every page. `useSession()` reaches `better-auth/react`, which
 * depends on `zod`, and that put **301 kB raw of a validation library** into
 * `/pl/regulamin` — a page with no form on it at all. Nothing on a marketing
 * page validates anything; it needs a name, an avatar and a role.
 *
 * So this asks the same endpoint `useSession` asks, and reads four fields off
 * the answer. Same request, same cookie, same server; what is gone is the
 * library that was only ever there to type it.
 *
 * ── ONE REQUEST, NOT ONE PER COMPONENT ───────────────────────────────────
 * `useSession` shares its state through a store, and two callers on one page
 * (the bar and the One Tap prompt) must not become two round trips. The promise
 * is the cache: the first caller starts it, everybody else awaits the same one,
 * and `refreshSessionUser()` is how a sign-out drops it.
 *
 * ── WHY NOT READ IT ON THE SERVER ────────────────────────────────────────
 * Because `cookies()` in the `/pl` layout turns fourteen prerendered marketing
 * pages dynamic to draw one avatar. That trade is refused deliberately — see
 * `app/pl/layout.tsx` — and the cost is the one frame where a signed-in visitor
 * sees the anonymous pill. The panel avoids that frame by passing the user in.
 */

export type SessionUser = {
  name?: string | null
  email: string
  image?: string | null
  role?: string | null
}

type SessionState = { user: SessionUser | null; pending: boolean }

let inflight: Promise<SessionUser | null> | null = null
let cached: SessionUser | null = null
let resolved = false

const listeners = new Set<(state: SessionState) => void>()

function announce() {
  const state: SessionState = { user: cached, pending: !resolved }
  for (const listener of listeners) listener(state)
}

/** Reads the four fields the chrome renders. Anything else is deliberately dropped. */
function readUser(payload: unknown): SessionUser | null {
  const user = (payload as { user?: unknown } | null)?.user
  if (!user || typeof user !== 'object') return null
  const email = (user as { email?: unknown }).email
  if (typeof email !== 'string') return null
  const { name, image, role } = user as { name?: unknown; image?: unknown; role?: unknown }
  return {
    email,
    name: typeof name === 'string' ? name : null,
    image: typeof image === 'string' ? image : null,
    role: typeof role === 'string' ? role : null,
  }
}

function load(): Promise<SessionUser | null> {
  inflight ??= fetch('/api/auth/get-session', { cache: 'no-store', credentials: 'same-origin' })
    .then((response) => (response.ok ? response.json() : null))
    .then((payload: unknown) => readUser(payload))
    // A session that cannot be read is an anonymous visitor, not an error worth
    // a console line on every page.
    .catch(() => null)
    .then((user) => {
      cached = user
      resolved = true
      announce()
      return user
    })
  return inflight
}

/** Drop the cached answer and ask again — after a sign-out, or a sign-in in place. */
export function refreshSessionUser(): void {
  inflight = null
  cached = null
  resolved = false
  announce()
  void load()
}

export function useSessionUser(initial?: SessionUser): SessionState {
  const [state, setState] = useState<SessionState>(() =>
    initial ? { user: initial, pending: false } : { user: cached, pending: !resolved },
  )

  useEffect(() => {
    const listener = (next: SessionState) => setState(next)
    listeners.add(listener)
    if (resolved) listener({ user: cached, pending: false })
    else void load()
    return () => {
      listeners.delete(listener)
    }
  }, [])

  // A server-resolved user always wins: the panel passes one precisely so the
  // anonymous pill never flashes there, and the fetch would undo that for a
  // frame.
  return initial ? { user: initial, pending: false } : state
}
